/**
 * 표 폭 맞춤(Fit-to-Width) 코어 — 가로 스크롤을 두지 않는 설계 제약을 결정론적으로 보장.
 *
 * 불변식: 표 열폭 합은 항상 예산(budget = 편집영역 가용폭) 이하. "폭 초과"는 스크롤이 없으므로
 * 화면 깨짐이지 대안이 아니다. 따라서 브라우저 CSS(max-width) 자동 축소에 맡기지 않고 에디터가
 * 열폭을 직접 계산한다.
 *
 *  - effMinCol: 열 수·예산에 따른 열 최소폭. 열이 많으면 기본최소(40) 미만 HARD_MIN(24)까지 낮춤.
 *  - fitWidths: shrink-only(작은 표는 늘리지 않음). Σ<=budget 보장, 각 열 >= effMin. compact 플래그
 *    (effMin<40 = 열이 많아 눌린 상태)로 렌더가 CSS min-width를 낮추도록 신호.
 */

export const HARD_MIN_COL = 24; // 물리 하한(이 아래로는 열 추가 차단)
export const DEFAULT_MIN_COL = 40; // 기본 최소폭(= CSS td min-width)

/** 열 수·예산 기반 열 최소폭. 예산 미상(0)이면 기본 최소로 폴백(측정 불가 환경 안전). */
export function effMinCol(cols, budget, capMin) {
  capMin = capMin || DEFAULT_MIN_COL;
  if (!budget || !cols || !isFinite(budget)) return capMin;
  return Math.max(HARD_MIN_COL, Math.min(capMin, Math.floor(budget / cols)));
}

/**
 * widths(현재/원하는 열폭 배열; null=auto)를 budget 이내로 축소.
 * @returns {{widths:(number|null)[], compact:boolean}}
 */
export function fitWidths(widths, budget, capMin) {
  capMin = capMin || DEFAULT_MIN_COL;
  if (!Array.isArray(widths) || !budget || !isFinite(budget)) return { widths, compact: false };
  const cols = widths.length;
  if (!cols) return { widths, compact: false };
  const nums = widths.map((w) => w || 0);
  const total = nums.reduce((a, b) => a + b, 0);
  const eff = effMinCol(cols, budget, capMin);
  if (total === 0) return { widths, compact: false }; // 전부 auto(null) → 그대로(CSS 자동분배)
  if (total <= budget) return { widths: widths.slice(), compact: eff < capMin }; // 축소 불필요(늘리지 않음)

  // 1) 비례 축소 후 effMin 바닥
  const f = budget / total;
  let out = nums.map((w) => Math.max(eff, Math.floor((w || eff) * f)));
  // 2) 바닥 때문에 합이 예산 초과 시, effMin 초과분(headroom)에서 비례 회수 → Σ<=budget 보장
  let excess = out.reduce((a, b) => a + b, 0) - budget;
  if (excess > 0) {
    const headroom = out.map((w) => w - eff);
    const hsum = headroom.reduce((a, b) => a + b, 0);
    if (hsum > 0) {
      out = out.map((w, i) => w - Math.floor((excess * headroom[i]) / hsum));
      // 반올림 잔차를 여유 있는 열에서 마저 회수
      let resid = out.reduce((a, b) => a + b, 0) - budget;
      for (let i = out.length - 1; i >= 0 && resid > 0; i--) {
        const take = Math.min(resid, out[i] - eff);
        out[i] -= take;
        resid -= take;
      }
    }
    // hsum===0(모든 열이 이미 effMin) = 물리 하한 초과(예산 < cols*HARD_MIN) → 불가피하게 예산 초과(compact)
  } else if (excess < 0) {
    // 바닥 반올림(Math.floor)으로 합이 예산에 미달 → 잔여(<열수 px)를 앞 열부터 +1씩 되채워 Σ=budget.
    // 비례 축소 브랜치(total>budget)에서만 동작하므로 목표가 정확히 budget이고, "스케일-투-맥스"가
    // 최대폭(=최초 fill 폭)에 정확히 도달한다. shrink-only의 total<=budget 조기반환은 이 위에서 이미 반환돼 불변.
    let deficit = -excess;
    for (let i = 0; deficit > 0 && i < out.length; i++) { out[i] += 1; deficit--; }
  }
  // 물리 하한 보장: 지배 열이 있는 초협소 예산(budget < cols*eff)에서 헤드룸 회수가 열을 eff(≥HARD_MIN)
  // 미만·음수로 밀 수 있다 → 최종적으로 eff로 클램프한다. 이 경우 Σ>budget(compact)는 불가피(문서화된 한계).
  out = out.map((w) => Math.max(eff, w));
  return { widths: out, compact: eff < capMin };
}
